import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import {
  bannerNotificationsQuery,
  dismissBannerNotificationMutation
} from 'Services';
import { COMPANIES } from 'Enums/Companies';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import type { BannerNotification } from 'componentsV2/feature-banner';

const FIVE_MINUTES = 5 * 60 * 1000;

// Companies opted out of banner notifications entirely — skip the request.
const EXCLUDED_COMPANY_IDS: number[] = [
  COMPANIES.BEACH_COMPANY_DEV,
  COMPANIES.BEACH_COMPANY_PROD
];

export const useBannerNotifications = () => {
  const { token, companyData } = useAppContext();
  const queryClient = useQueryClient();
  const query = bannerNotificationsQuery();

  const companyId = companyData?.ID;
  // Wait for company data so an excluded company never fires the request
  // during the metadata-loading window.
  const isEnabled =
    !!token && !!companyId && !EXCLUDED_COMPANY_IDS.includes(companyId);

  const { data = [] } = useQuery({
    ...query,
    enabled: isEnabled, // gate behind auth-ready signal + company opt-out
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1
  });

  // Dismissal is persisted server-side. Optimistically drop the banner from the
  // cache so it disappears instantly; on failure restore it and surface a
  // non-blocking error. Idempotent server-side, so no dedupe needed here.
  const { mutate: dismiss } = useMutation({
    ...dismissBannerNotificationMutation(),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: query.queryKey });
      const previous = queryClient.getQueryData<BannerNotification[]>(
        query.queryKey
      );
      queryClient.setQueryData<BannerNotification[]>(
        query.queryKey,
        (old = []) => old.filter(notification => notification.id !== id)
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous)
        queryClient.setQueryData(query.queryKey, context.previous);
      enqueueSnackbar("Couldn't dismiss the banner. Please try again.", {
        variant: 'error'
      });
    }
  });

  return { notifications: data, dismiss };
};
